import { getCountryCallingCode, isSupportedCountry, parsePhoneNumber as parse } from 'react-phone-number-input'
import type { Iso3166Alpha2 } from './locations'
import { getAlpha2From3, getAlpha3From2 } from './locations'
import supportedCallingLocations from './supportedCallingLocations'
import type { PhoneAlpha2, PhoneAlpha3, ParsePhoneNumber, ParsedPhoneNumber } from './types'

const empty: ParsedPhoneNumber = {
  number: null,
  callingCode: null,
  nationalNumber: null,
  location: null,
  alpha2: null,
  alpha3: null,
  format: () => '',
  isValid: () => false,
  isSupported: () => false,
}

// rawCode here is just a hint since parsing may determine another location based on the area code
// See https://en.wikipedia.org/wiki/List_of_country_calling_codes for list of area codes.
// For example: American Samoa +1 684, Bermuda +1 441, Saint Kitts and Nevis +1 869
const parsePhoneNumber: ParsePhoneNumber = function parsePhoneNumber(
  phoneNumber,
  defaultLocation,
  { throwErrors = false } = {}
): ParsedPhoneNumber {
  try {
    let alpha2
    let alpha3
    if (defaultLocation) {
      alpha2 = defaultLocation.toUpperCase()
      if (alpha2.length === 3) {
        alpha3 = alpha2
        alpha2 = getAlpha2From3(alpha3)
      }
    }
    let phoneNumberObj = parse(String(phoneNumber), alpha2)
    if (!phoneNumberObj) {
      return empty
    }
    // Handle international number case where couldn't detect country on first pass and try parsing as a national number instead
    // This would mainly be for locations that share calling codes where the parser does not recognize the area code
    // so it can't say for sure the location. (e.g. US 222-222-2222 )
    if (
      !phoneNumberObj.country &&
      alpha2 &&
      // add test that unsupported alpha2 still returns data
      isSupportedCountry(alpha2) &&
      getCountryCallingCode(alpha2) === phoneNumberObj.countryCallingCode
    ) {
      phoneNumberObj = parse(phoneNumberObj.nationalNumber as string, alpha2) ?? phoneNumberObj
    }
    return {
      number: phoneNumberObj.number as string,
      callingCode: phoneNumberObj.countryCallingCode as string,
      nationalNumber: phoneNumberObj.nationalNumber as string,
      // "location" is available as "location" or "alpha2"--"alpha2" to distinguish from "alpha3" but also as "location" since that's the most natural
      location: phoneNumberObj.country as PhoneAlpha2,
      alpha2: phoneNumberObj.country as PhoneAlpha2,
      // alpha3 could be undefined if an unofficial alpha2 is parsed
      alpha3: getAlpha3From2(phoneNumberObj.country as Iso3166Alpha2) as PhoneAlpha3,
      // Passing ..rest as an escape hatch but not documenting for now until we have a use case
      format: (format = 'INTERNATIONAL', ...rest) => phoneNumberObj.format(format, ...rest),
      isValid(lengthOnlyCheck = false) {
        if (lengthOnlyCheck) {
          // avoiding potentially outdated validation metadata
          return phoneNumberObj.isPossible()
        }
        return phoneNumberObj.isValid()
      },
      isSupported(supportedLocations = supportedCallingLocations) {
        // If only performing lengthOnlyCheck then international numbers without
        // a location specificed by the user may pass as valid but still have alpha2/alpha3 undefined
        // (this mainly applies to location that share calling codes--see https://en.wikipedia.org/wiki/List_of_country_calling_codes)
        return !!this.alpha2 && !!this.alpha3 && supportedLocations.includes(this.alpha2)
      },
    }
  } catch (error) {
    if (throwErrors) {
      throw error
    }
    return empty
  }
}

export default parsePhoneNumber
